import React, { useState, useRef } from 'react'
import Layout from '../../Layout'
import { Tabs, Button, Space, Modal, Input, Alert, DatePicker, Typography, Select, Card, message, Col, Row } from 'antd'
import CurrencyInput from '../../CurrencyInput'
import Header from '../../Header'
import InvoiceSummaryTab from '../InvoiceSummaryTab'
import { EditOutlined, SendOutlined, CloseSquareFilled, FileOutlined } from '@ant-design/icons'
import useAxios from 'axios-hooks'
import InvoiceStatus from '../InvoiceStatus'
import { StatusBar } from './style'
import moment from 'moment'
import { format } from 'date-fns'

const DATE_FORMAT = 'DD/MM/YYYY'

// Disable future dates in DatePicker
const disabledDate = (current) => {
  return current && current > moment().endOf('day');
};

const InvoiceSummary = ({
  invoice_group,
  receipt_summaries,
  lender_received,
  noburo_received,
  lender,
  partner,
  due_date,
  overdue,
  email: currentEmail
}) => {
  const receipt_summary = receipt_summaries[0]

  const { Title, Text } = Typography
  const [isModalEmail, setIsModalEmail] = useState(false)
  const [isModalDate, setIsModalDate] = useState(false)
  const [reportDate, setReportDate] = useState(receipt_summary?.report_date ? new Date(receipt_summary.report_date) : '')
  const [dueDate, setDueDate] = useState('')
  const [transferDate, setTransferDate] = useState(receipt_summary?.transfer_date ? new Date(receipt_summary.transfer_date) : '')
  const [transferDateNoburo, setTransferDateNoburo] = useState(receipt_summary?.transfer_date_noburo ? new Date(receipt_summary.transfer_date_noburo) : '')
  const emailRef = useRef('')
  const [pageSize, setPageSize] = useState(20)
  const [lenderReceived, setLenderReceived] = useState(receipt_summary?.lender_received ?? lender_received)
  const [noburoReceived, setNoburoReceived] = useState(receipt_summary?.noburo_received ?? noburo_received)
  const [isModalStatusOpen, setIsModalStatusOpen] = useState(false)
  const [status, setStatus] = useState(['paid', 'partially_paid'].includes(invoice_group.status) ? 'rv_created' : invoice_group.status)
  const [statusView, setStatusView] = useState(invoice_group.status)
  const invoice_group_id = invoice_group.id

  const [{ loading: loadingUpdateStatus }, updateStatus] = useAxios({
    url: '/admins/api/invoice_groups/update',
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' }
  }, { manual: true })

  const [{ loading, error }, sendEmail] = useAxios({
    url: `/admins/api/invoice_groups/${invoice_group_id}/send_email`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  }, { manual: true })

  // Modal handlers
  const showModalStatusOpen = () => setIsModalStatusOpen(true)
  const handleCancelStatusOpen = () => setIsModalStatusOpen(false)
  const handleChangeSelect = (value) => setStatus(value)

  // Status change handler
  const handleOkStatusOpen = async () => {
    const payload = {
      status,
      invoice_group_ids: [invoice_group.id],
    }

    // Validation for 'rv_created' status
    if (status === 'rv_created') {
      if (!reportDate) {
        message.error('กรุณากรอกวันตัดรอบ')
        return
      }

      const lenderReceivedValid = parseFloat(lenderReceived) > 0 ? transferDate : !transferDate
      const noburoReceivedValid = parseFloat(noburoReceived) > 0 ? transferDateNoburo : !transferDateNoburo

      if (!lenderReceivedValid || !noburoReceivedValid) {
        message.error('กรุณากรอกยอดเงินที่เข้าอย่างใดอย่างหนึ่ง')
        return
      }

      payload.lender_received = +lenderReceived || 0
      payload.noburo_received = +noburoReceived || 0

      if (transferDate) payload.transfer_date = transferDate && format(transferDate, 'yyyy-MM-dd')
      if (transferDateNoburo) payload.transfer_date_noburo = transferDateNoburo && format(transferDateNoburo, 'yyyy-MM-dd')
      if (reportDate) payload.report_date = format(reportDate, 'yyyy-MM-dd')

      payload.status = transferDate && transferDateNoburo ? 'paid' : 'partially_paid'
    }

    try {
      await updateStatus({ data: JSON.stringify(payload) })
      setStatusView(payload.status)

      if (status === 'rv_created') {
        message.success({
          content: 'สร้างใบรับเงินเรียบร้อย คลิ๊กเพื่อตรวจสอบ',
          duration: 5,
          onClick: () => window.open('/admins/receipt/receipt_summaries'),
        })
      }
    } catch (e) {
      message.error({
        content: e.message,
        duration: 5
      })
    }
    setIsModalStatusOpen(false)
  }

  const optionSelect = [
    { id: 0, value: 'bn_created', label: 'สร้างใบเรียกเก็บแล้ว' },
    { id: 3, value: 'sent', label: 'รอรับชำระ' },
    { id: 4, value: 'rv_created', label: 'ชำระแล้ว' },
    { id: 5, value: 'cancelled', label: 'ยกเลิก' }
  ]

  const confirmSendEmail = async () => {
    try {
      await sendEmail({
        data: JSON.stringify({
          email: emailRef.current.input.value
        })
      })
      setIsModalEmail(false)
    } catch (e) {
      console.log(e)
    }
  }

  const onChangeDate = (date, dateString) => setDueDate(dateString)

  const confirmDate = async () => {
    try {
      await updateStatus({
        data: JSON.stringify({
          due_date: dueDate,
          invoice_group_ids: [invoice_group.id],
        })
      })

      setIsModalDate(false)
    } catch (e) {
      console.log(e)
    }
  }

  const items = [
    {
      key: '1',
      label: 'Not Resign',
      children: (
        <InvoiceSummaryTab
          resign={false}
          invoice_group_id={invoice_group_id}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      ),
    },
    {
      key: '2',
      label: 'Resigned',
      children: (
        <InvoiceSummaryTab
          resign={true}
          invoice_group_id={invoice_group_id}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      ),
    }
  ]

  return (
    <Layout>
      <StatusBar>
        <InvoiceStatus status={statusView === 'sent' ? (overdue ? 'overdue' : statusView) : statusView} size="large" />
      </StatusBar>
      <Header title={`ใบเรียกเก็บเงินแทน : ${invoice_group.no} `}>
        <Space size="small">
          <Button type="primary" size="large" icon={<SendOutlined />} onClick={() => setIsModalEmail(true)}>
            ส่งอีเมล
          </Button>
          <Button type="primary" size="large" icon={<EditOutlined />} onClick={() => setIsModalDate(true)}>
            แก้ไขวันที่ครบกำหนด
          </Button>
          <Button type="primary" size="large" icon={<FileOutlined />} onClick={() => window.open(`${invoice_group_id}/preview`)}>
            ตัวอย่าง
          </Button>
          <Button onClick={showModalStatusOpen} type="secondary" size="large">
            เปลี่ยนสถานะ
          </Button>
          <Button type="default" size="large" style={{ marginRight: '10px' }}>
            ลบ
          </Button>
        </Space>
      </Header>

      {receipt_summaries.map((rv) => (
        <React.Fragment key={rv.id}>
          <a target="_blank" rel="noreferrer" href={`/admins/receipt/receipt_summaries/${rv.id}`}>
            {rv.tax_id}
          </a>
          <br />
        </React.Fragment>
      ))}

      <Card
        title={`${lender.name} (${lender.lms_project_id})`}
        extra={<a href={`/admins/lenders/${lender.id}/edit`}>แก้ไข</a>}
        headStyle={{
          fontFamily: 'SukhumvitSet-Bold',
          background: '#efefef'
        }}
      >
        <Row gutter={16}>
          <Col className="gutter-row" span={12}>
            <Card
              title="ชื่อลูกค้า"
              extra={<a href={`/admins/partners/${partner.id}/edit`}>แก้ไข</a>}
            >
              <Space direction="vertical" size={0}>
                <Title style={{ fontFamily: 'SukhumvitSet-Bold' }} level={5}>{partner.name}</Title>
                <Text>
                  {partner.tax_id ? partner.tax_id : 'ไม่พบเลขผู้่เสียภาษี'} ({partner.branch ? partner.branch : 'สำนักงานใหญ่'})
                </Text>
                <Text>ที่อยู่ : {partner.address}</Text>
                <Text>
                  {partner.subdistrict} {partner.district} {partner.province} {partner.post_code}
                </Text>
              </Space>
            </Card>
          </Col>
          <Col className="gutter-row" span={12} style={{ display: 'flex' }}>
            <Card title="ข้อมูลเอกสาร" style={{ flex: 1 }}>
              <div>
                <Space direction="vertical">
                  <Text>
                    วันที่ : {invoice_group.created_at && format(new Date(invoice_group.created_at), 'yyyy-MM-dd')}
                  </Text>
                  <Text>วัันครบกำหนดชำระ : {due_date}</Text>
                </Space>
              </div>
            </Card>
          </Col>
        </Row>
      </Card>

      <Tabs size="large" defaultActiveKey="1" items={items} />

      {/* Email Modal */}
      <Modal title="ส่งอีเมล" open={isModalEmail} footer={null} onCancel={() => setIsModalEmail(false)}>
        <Input size="large" ref={emailRef} defaultValue={currentEmail} placeholder="กรอกอีเมล" />
        {error && (
          <Alert
            message="Error Text"
            description={error.message}
            type="error"
            style={{ marginTop: '10px' }}
            closable={{
              'aria-label': 'close', closeIcon: <CloseSquareFilled />,
            }}
          />
        )}
        <Button type="primary" size="large" style={{ marginTop: '10px' }} loading={loading} onClick={confirmSendEmail}>
          ส่งอีเมล
        </Button>
      </Modal>

      {/* Due Date Modal */}
      <Modal title="แก้ไขวันที่ครบกำหนด" open={isModalDate} footer={null} onCancel={() => setIsModalDate(false)}>
        <DatePicker
          onChange={onChangeDate}
          defaultValue={moment(new Date(invoice_group.due_date), DATE_FORMAT)}
          needConfirm
        />
        <Button type="primary" size="large" style={{ marginTop: '10px' }} loading={loading} onClick={confirmDate}>
          แก้ไข
        </Button>
      </Modal>

      {/* Status Modal */}
      <Modal title="เปลี่ยนสถานะ" open={isModalStatusOpen} footer={null} onCancel={handleCancelStatusOpen}>
        <Space direction="vertical">
          <div>
            <Select
              style={{ width: '472px' }}
              size="large"
              placeholder="เลือกสถานะ"
              defaultValue={status}
              onChange={handleChangeSelect}
              options={optionSelect}
            />
          </div>
          {status === 'rv_created' && (
            <Card title="รายละเอียด สร้างใบรับเงิน" size="small" style={{ width: '472px' }}>
              <Space direction="vertical">
                <div>วันตัดรอบ:</div>
                <DatePicker
                  defaultValue={reportDate ? moment(reportDate, DATE_FORMAT) : ''}
                  format={DATE_FORMAT}
                  size="large"
                  onChange={(event) => setReportDate(event?._d)}
                />
                <Space>
                  <div style={{ width: 180 }}>
                    <div>วันที่สถาบันการเงินได้รับเงิน:</div>
                    <DatePicker
                      defaultValue={transferDate ? moment(transferDate, DATE_FORMAT) : ''}
                      format={DATE_FORMAT}
                      size="large"
                      disabledDate={disabledDate}
                      onChange={(event) => setTransferDate(event?._d)}
                    />
                  </div>
                  <div>
                    <div>เข้าบัญชีสถาบันการเงิน (บาท):</div>
                    <CurrencyInput
                      size="large"
                      value={lenderReceived}
                      showSymbol={false}
                      initialColor="blue"
                      onChange={val => setLenderReceived(val)}
                    />
                  </div>
                </Space>
                <Space>
                  <div style={{ width: 180 }}>
                    <div>วันที่ noburo ได้รับเงิน:</div>
                    <DatePicker
                      defaultValue={transferDateNoburo ? moment(transferDateNoburo, DATE_FORMAT) : ''}
                      format={DATE_FORMAT}
                      size="large"
                      disabledDate={disabledDate}
                      onChange={(event) => setTransferDateNoburo(event?._d)}
                    />
                  </div>
                  <div>
                    <div>เข้าบัญชี noburo (บาท):</div>
                    <CurrencyInput
                      size="large"
                      value={noburoReceived}
                      showSymbol={false}
                      initialColor="blue"
                      onChange={val => setNoburoReceived(val)}
                    />
                  </div>
                </Space>
              </Space>
            </Card>
          )}
          <Button
            size="large"
            style={{ marginTop: '10px' }}
            type="primary"
            loading={loadingUpdateStatus}
            onClick={handleOkStatusOpen}
          >
            ตกลง
          </Button>
        </Space>
      </Modal>
    </Layout>
  )
}

export default InvoiceSummary
